/**
 * Shared audio player — one place in the whole app to turn a Blob (mp3) into
 * sound. There is a single active source at any time: submitting new audio ends
 * whatever was playing before. The player owns only the audio + its state; it
 * never touches the DOM. UI (the round play button, the per-sentence highlight)
 * reacts through {@link subscribe}, so the player "controls the icon" by driving
 * the state the icon reflects.
 *
 * Blobs come from IndexedDB caches keyed by an id (`topics.audio_blob`,
 * `phrases.audio_blob`, …). Callers resolve the Blob and hand it here; the
 * player stays agnostic about where it came from.
 *
 * Usage:
 *   play(blob)                              // one clip
 *   play([{ id, blob }, { id, blob }, …])   // a queue, played in order
 *   play(queue, { onComplete })             // + notified when it drains on its own
 *   pause() / resume() / stop()
 *   subscribe(({ status, currentId }) => …) // 'idle' | 'playing' | 'paused'
 */

/** @type {HTMLAudioElement|null} */
let audio = null;
/** Remaining items to play after the current one: `{ id, blob }`. */
let queue = [];
/** Object URL backing the current clip — revoked when it's replaced. */
let objectUrl = null;
let currentId = null;
let status = 'idle'; // 'idle' | 'playing' | 'paused'
/**
 * Callback for the current queue draining on its own, if the caller asked for
 * one. `status` alone can't carry this: a natural end and an explicit
 * {@link stop} both land on `'idle'`, and only the former means "heard it all".
 * @type {?() => void}
 */
let onComplete = null;
/**
 * Whether the clip that just finished ended by itself, as opposed to erroring
 * out. Guards the completion signal, so a queue whose last clip failed to decode
 * (or one that had nothing playable at all) doesn't count as fully heard.
 */
let lastClipEnded = false;

/** @type {Set<(state: {status: string, currentId: ?string}) => void>} */
const listeners = new Set();

/** Current snapshot handed to every subscriber. */
function snapshot() {
  return { status, currentId };
}

function emit() {
  const state = snapshot();
  listeners.forEach((fn) => fn(state));
}

/**
 * Observe player state. The listener fires immediately with the current state
 * and on every change afterwards. Returns an unsubscribe function.
 */
export function subscribe(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

export function getStatus() {
  return status;
}

/** Tear down the current audio element and its object URL (leaves the queue). */
function teardown() {
  if (audio) {
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio = null;
  }
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
}

/**
 * Stop everything, drop the queue, and go idle. Safe to call at any time.
 * Discards any pending completion callback **without** firing it — an
 * interrupted queue was not heard to the end.
 */
export function stop() {
  teardown();
  queue = [];
  currentId = null;
  status = 'idle';
  onComplete = null;
  lastClipEnded = false;
  emit();
}

/** Advance to the next item that actually has a blob; stop when the queue drains. */
function playNext() {
  teardown();

  let item = queue.shift();
  while (item && !(item.blob instanceof Blob)) item = queue.shift(); // skip missing clips
  if (!item) {
    // Drained. Hold on to the callback across `stop()` (which clears it) so the
    // listener runs against the settled 'idle' state, and only when the last
    // clip really finished playing.
    const completed = lastClipEnded ? onComplete : null;
    stop();
    completed?.();
    return;
  }

  objectUrl = URL.createObjectURL(item.blob);
  currentId = item.id ?? null;
  lastClipEnded = false; // reset per clip, so the flag always describes this one
  audio = new Audio(objectUrl);
  audio.onended = () => {
    lastClipEnded = true;
    playNext();
  };
  audio.onerror = () => playNext(); // decode/network glitch → move on, uncounted
  status = 'playing';
  emit();
  audio.play().catch(() => playNext()); // autoplay blocked / bad data → skip
}

/**
 * Start playing. Ends any current playback first.
 * @param {Blob | Array<{id: ?string, blob: ?Blob}>} input A single blob or a
 *   playlist; playlist items without a blob are skipped.
 * @param {{ onComplete?: () => void }} [options]
 *   - onComplete: called once when this queue plays through to its end. Not
 *     called if playback is stopped or replaced first, nor if the final clip
 *     errored out — so it means "the listener heard the whole thing".
 */
export function play(input, { onComplete: complete } = {}) {
  teardown();
  if (input instanceof Blob) {
    queue = [{ id: null, blob: input }];
  } else if (Array.isArray(input)) {
    queue = input.slice();
  } else {
    queue = [];
  }
  currentId = null;
  onComplete = typeof complete === 'function' ? complete : null;
  lastClipEnded = false;
  playNext();
}

export function pause() {
  if (status !== 'playing' || !audio) return;
  audio.pause();
  status = 'paused';
  emit();
}

export function resume() {
  if (status !== 'paused' || !audio) return;
  status = 'playing';
  emit();
  audio.play().catch(() => playNext());
}
